import React, { useState, useEffect } from 'react';

// ============ INLINE SVG ICONS ============
import {
  ShieldIcon, ZapIcon, ChartIcon, LinkIcon, CheckIcon, CalendarIcon,
  PillIcon, CardIcon, LockIcon, KeyIcon, ScrollIcon, RefreshIcon, MessageIcon,
  BuildingIcon, UsersIcon, StethoscopeIcon, ActivityIcon, ArrowIcon, ChevronIcon,
  SparkleIcon, DatabaseIcon, GlobeIcon, ClockIcon, AwardIcon, TrendIcon, MailIcon} from '../../constants/svgIcons';
import {useTranslation} from "react-i18next";

const CliniVista = () => {

  const [scrollY, setScrollY] = useState(0);
  // const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const {t } = useTranslation();
  const lang = (key) => t(`CliniVistaPage.${key}`);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const whyFeatures = [
    {
      icon: <BuildingIcon className="w-7 h-7" />,
      title: lang('why.features.uaeFirstDesign.title'),
      desc: lang('why.features.uaeFirstDesign.desc'),
      gradient: 'from-emerald-500/10 to-teal-500/5'
    },
    {
      icon: <LinkIcon className="w-7 h-7" />,
      title: lang('why.features.nabidhIntegrated.title'),
      desc: lang('why.features.nabidhIntegrated.desc'),
      gradient: 'from-blue-500/10 to-cyan-500/5'
    },
    {
      icon: <ZapIcon className="w-7 h-7" />,
      title: lang('why.features.fastIntuitive.title'),
      desc: lang('why.features.fastIntuitive.desc'),
      gradient: 'from-amber-500/10 to-orange-500/5'
    },
    {
      icon: <ChartIcon className="w-7 h-7" />,
      title: lang('why.features.realtimeAnalytics.title'),
      desc: lang('why.features.realtimeAnalytics.desc'),
      gradient: 'from-purple-500/10 to-pink-500/5'
    }
  ];

  const features = [
    {
      icon: <UsersIcon className="w-6 h-6" />,
      title: lang('features.patientManagement.title'),
      tagline: lang('features.patientManagement.tagline'),
      points: [
        lang('features.patientManagement.points.emiratesId'),
        lang('features.patientManagement.points.unifiedTimeline'),
        lang('features.patientManagement.points.customisableForms'),
        lang('features.patientManagement.points.appointmentReminders')
      ],
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80'
    },
    {
      icon: <CalendarIcon className="w-6 h-6" />,
      title: lang('features.appointmentScheduling.title'),
      tagline: lang('features.appointmentScheduling.tagline'),
      points: [
        lang('features.appointmentScheduling.points.dragDropCalendar'),
        lang('features.appointmentScheduling.points.googleCalendar'),
        lang('features.appointmentScheduling.points.automatedConfirmations'),
        lang('features.appointmentScheduling.points.waitlistManagement'),
        lang('features.appointmentScheduling.points.doctorAvailability')
      ],
      image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=900&q=80'
    },
    {
      icon: <StethoscopeIcon className="w-6 h-6" />,
      title: lang('features.clinicalDocumentation.title'),
      tagline: lang('features.clinicalDocumentation.tagline'),
      points: [
        lang('features.clinicalDocumentation.points.soapTemplates'),
        lang('features.clinicalDocumentation.points.bodyDiagram'),
        lang('features.clinicalDocumentation.points.customisableForms')
      ],
      image: 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?w=900&q=80'
    },
    {
      icon: <PillIcon className="w-6 h-6" />,
      title: lang('features.ePrescriptions.title'),
      tagline: lang('features.ePrescriptions.tagline'),
      points: [
        lang('features.ePrescriptions.points.eRxGeneration'),
        lang('features.ePrescriptions.points.drugInteractionAlerts'),
        lang('features.ePrescriptions.points.pharmacyDispensing'),
        lang('features.ePrescriptions.points.controlledDrugs'),
        lang('features.ePrescriptions.points.biDirectionalComm')
      ],
      image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=900&q=80'
    },
    {
      icon: <CardIcon className="w-6 h-6" />,
      title: lang('features.billingFinance.title'),
      tagline: lang('features.billingFinance.tagline'),
      points: [
        lang('features.billingFinance.points.oneClickInvoice'),
        lang('features.billingFinance.points.vatCompliant'),
        lang('features.billingFinance.points.paymentProcessing'),
        lang('features.billingFinance.points.accountingSync'),
        lang('features.billingFinance.points.revenueDashboards'),
        lang('features.billingFinance.points.auditTrail')
      ],
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80'
    }
  ];

  const securityFeatures = [
    { icon: <LockIcon className="w-6 h-6" />, title: lang('security.features.encryption.title'), desc: lang('security.features.encryption.desc') },
    { icon: <KeyIcon className="w-6 h-6" />, title: lang('security.features.roleBasedAccess.title'), desc: lang('security.features.roleBasedAccess.desc') },
    { icon: <ScrollIcon className="w-6 h-6" />, title: lang('security.features.auditTrails.title'), desc: lang('security.features.auditTrails.desc') },
    { icon: <RefreshIcon className="w-6 h-6" />, title: lang('security.features.automatedBackups.title'), desc: lang('security.features.automatedBackups.desc') }
  ];

  const integrations = [
    { name: lang('integrations.items.nabidh.name'), desc: lang('integrations.items.nabidh.desc'), icon: <DatabaseIcon className="w-5 h-5" /> },
    { name: lang('integrations.items.whatsappSms.name'), desc: lang('integrations.items.whatsappSms.desc'), icon: <MessageIcon className="w-5 h-5" /> },
    { name: lang('integrations.items.pharmacy.name'), desc: lang('integrations.items.pharmacy.desc'), icon: <PillIcon className="w-5 h-5" /> },
    { name: lang('integrations.items.finance.name'), desc: lang('integrations.items.finance.desc'), icon: <CardIcon className="w-5 h-5" /> }
  ];

  const nabidhBenefits = [
    lang('nabidh.benefits.realtimeSubmission'),
    lang('nabidh.benefits.automatedSync'),
    lang('nabidh.benefits.consolidatedHistory'),
    lang('nabidh.benefits.dhaCompliance'),
    lang('nabidh.benefits.secureExchange'),
    lang('nabidh.benefits.automaticUpdates')
  ];

  return (
    <div>
      <div className="bg-theme-color min-h-screen font-sans antialiased ngar-pae">
        <div className="absolute inset-0 grid-pattern"></div>
        <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
        .pulse-dot::after {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 9999px;
          background: rgba(34, 197, 94, 0.3);
          animation: pulse-ring 2s ease-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .float-anim { animation: float 6s ease-in-out infinite; }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee { animation: marquee 30s linear infinite; }
        .fade-up { opacity: 0; transform: translateY(30px); transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); }
        .fade-up.visible { opacity: 1; transform: translateY(0); }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        .blob-anim { animation: blob 12s ease-in-out infinite; }
      `}</style>

        {/* ============ HERO ============ */}
        <section className="relative pt-32 md:pt-40 pb-20 md:pb-16 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-100/40 to-transparent rounded-full blur-3xl -z-10 blob-anim"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-emerald-100/30 to-transparent rounded-full blur-3xl -z-10 blob-anim" style={{ animationDelay: '4s' }}></div>
          <div className="relative w-mobile-container md:w-theme-container mx-auto">
            <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 bg-white border border-theme-color/10 rounded-full px-4 py-1.5 mb-7 shadow-sm">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                  <span className="text-xs font-semibold tracking-wider text-theme-color/80">{lang('badges.dhaApproved')}</span>
                </div>
                <h1 className="font-display font-medium text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white">
                  {lang('hero.titlePart1')}{' '}
                  <span className="font-bold text-white">{lang('hero.titleHighlight')}</span>{' '}
                  {lang('hero.titlePart2')}
                </h1>
                <p className="mt-7 text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
                  {lang('hero.description')}
                </p>
                <div className="flex flex-wrap mt-5 gap-4">
                  <a href="#contact" className="group inline-flex items-center justify-center gap-2 bg-theme-secondary text-white px-7 py-4 rounded-full font-semibold hover:bg-theme-secondary transition-all hover:shadow-xl hover:shadow-theme-color/25">
                    {lang('hero.scheduleDemo')}
                    <ArrowIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a href="#features" className="inline-flex items-center justify-center gap-2 bg-white border border-theme-color/15 text-theme-color px-7 py-4 rounded-full font-semibold hover:border-theme-color/30 transition-all">
                    {lang('hero.exploreFeatures')}
                  </a>
                </div>
                <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 pt-8 border-t border-theme-color/10">
                  {[
                    { num: '100%', label: lang('hero.stats.dhaCompliant') },
                    { num: '24/7', label: lang('hero.stats.realtimeSync') },
                    { num: 'AES-256', label: lang('hero.stats.encryption') }
                  ].map((stat, i) => (
                    <div key={i}>
                      <div className="font-display font-semibold text-2xl text-theme-color">{stat.num}</div>
                      <div className="text-xs uppercase tracking-wider text-theme-color/55 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-5 relative">
                <div className="relative">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-theme-color/20">
                    <img
                      src="https://i.pinimg.com/originals/1e/14/5b/1e145b7b9133b8d24cd7184a8208621d.jpg"
                      alt={lang('hero.imageAlt')}
                      className="w-full h-[420px] sm:h-[500px] md:h-[560px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-theme-color/40 via-transparent to-transparent"></div>
                  </div>
                  <div className="absolute sm:-left-1 md:-left-12 top-8 md:top-12 bg-white rounded-2xl shadow-xl shadow-theme-color/10 p-3 sm:p-4 border border-theme-color/5 float-anim w-48 sm:w-56" style={{ animationDelay: '0s' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                        <CheckIcon className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] sm:text-xs text-theme-color/55 uppercase tracking-wider">{lang('hero.floatingCards.nabidhSync')}</div>
                        <div className="text-xs sm:text-sm font-semibold text-theme-color truncate">{lang('hero.floatingCards.nabidhSyncStatus')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute sm:-right-1 md:-right-8 bottom-36 md:bottom-36 bg-white rounded-2xl shadow-xl shadow-theme-color/10 p-3 sm:p-4 border border-theme-color/5 float-anim w-52 sm:w-60" style={{ animationDelay: '2s' }}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <ActivityIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] sm:text-xs text-theme-color/55 uppercase tracking-wider">{lang('hero.floatingCards.todaysVisits')}</div>
                        <div className="text-xs sm:text-sm font-semibold text-theme-color">{lang('hero.floatingCards.todaysVisitsValue')}</div>
                        <div className="mt-1.5 flex gap-1">
                          {[...Array(8)].map((_, i) => (
                            <div key={i} className={`h-1 w-3 rounded-full ${i < 6 ? 'bg-blue-500' : 'bg-blue-100'}`}></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -left-2 md:-left-6 bottom-2 md:bottom-10 bg-white text-theme-color rounded-2xl shadow-xl p-3 sm:p-4 w-44 sm:w-52 float-anim" style={{ animationDelay: '4s' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <SparkleIcon className="w-4 h-4 text-amber-300" />
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-600">{lang('hero.floatingCards.today')}</span>
                    </div>
                    <div className="text-sm font-semibold inline">{lang('hero.floatingCards.revenueValue')} | </div>
                    <div className="text-xs text-slate-600 mt-0.5 inline">{lang('hero.floatingCards.revenueChange')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TRUST MARQUEE ============ */}
        <section className="w-mobile-container md:w-theme-container mx-auto py-8 border-y border-theme-color/10 !bg-theme-color overflow-hidden">
          <div className="flex items-center gap-16 marquee whitespace-nowrap text-white text-sm font-medium tracking-wider uppercase">
            {[...Array(2)].map((_, i) => (
              <React.Fragment key={i}>
                <span className="flex items-center gap-2"><ShieldIcon className="w-4 h-4" /> {lang('badges.dhaApprovedShort')}</span>
                <span>|</span>
                <span className="flex items-center gap-2"><DatabaseIcon className="w-4 h-4" /> {lang('badges.nabidhIntegrated')}</span>
                <span>|</span>
                <span className="flex items-center gap-2"><LockIcon className="w-4 h-4" /> {lang('badges.aesEncrypted')}</span>
                <span>|</span>
                <span className="flex items-center gap-2"><AwardIcon className="w-4 h-4" /> {lang('badges.vatCompliant')}</span>
                <span>|</span>
                <span className="flex items-center gap-2"><GlobeIcon className="w-4 h-4" /> {lang('badges.uaeFirstDesignBadge')}</span>
                <span>|</span>
                <span className="flex items-center gap-2"><ClockIcon className="w-4 h-4" /> {lang('badges.support247')}</span>
                <span>|</span>
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* ============ WHY CLINIVISTA ============ */}
        <section id="why" data-animate className={`bg-white py-20 md:py-32 fade-up ${isVisible.why ? 'visible' : ''}`}>
          <div className="w-mobile-container md:w-theme-container mx-auto">
            <div className="max-w-2xl mb-16">
              <div className="text-xs font-semibold tracking-[0.25em] text-theme-secondary uppercase mb-4">{lang('why.eyebrow')}</div>
              <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl leading-tight text-theme-color">
                {lang('why.titlePart1')}{' '}
                <span className="text-theme-secondary">{lang('why.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-base md:text-lg text-theme-color/65 leading-relaxed">
                {lang('why.description')}
              </p>
            </div>
            <div className="grid sm:grid-cols-1 lg:grid-cols-4 gap-5">
              {whyFeatures.map((f, i) => (
                <div key={i} className="group relative bg-white border border-theme-color/8 rounded-3xl p-6 sm:p-7 hover:border-theme-color/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-theme-color/8 overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${f.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-theme-color/5 flex items-center justify-center text-theme-color group-hover:bg-theme-color group-hover:text-white transition-all duration-500 mb-5">
                      {f.icon}
                    </div>
                    <h3 className="font-display font-semibold text-xl text-theme-color mb-2">{f.title}</h3>
                    <p className="text-sm text-theme-color/65 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ NABIDH SECTION ============ */}
        <section id="nabidh" data-animate className={`relative py-20 md:py-32 bg-theme-color text-white overflow-hidden fade-up ${isVisible.nabidh ? 'visible' : ''}`}>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-blue-500/30 rounded-full blur-3xl blob-anim"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/20 rounded-full blur-3xl blob-anim" style={{ animationDelay: '5s' }}></div>
          </div>
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.08) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>

          <div className="relative w-mobile-container md:w-theme-container mx-auto">
            <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-6">
                  <CheckIcon className="w-4 h-4 text-emerald-300" />
                  <span className="text-xs font-semibold tracking-wider text-emerald-200">{lang('badges.dhaApprovedFull')}</span>
                </div>
                <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-6">
                  {lang('nabidh.titlePart1')} {' '}
                  <span className="text-blue-200">{lang('nabidh.titleHighlight')}</span>{' '}
                  {lang('nabidh.titlePart2')}
                </h2>
                <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mb-10">
                  {lang('nabidh.description')}
                </p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {nabidhBenefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckIcon className="w-3.5 h-3.5 text-emerald-300" />
                      </div>
                      <span className="text-sm text-white/85 leading-relaxed">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-5 relative">
                <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/50 mb-1">{lang('nabidh.statusCard.connectionStatus')}</div>
                      <div className="font-display text-2xl font-medium">{lang('nabidh.statusCard.nabidhLive')}</div>
                    </div>
                    <div className="relative">
                      <div className="w-3 h-3 rounded-full bg-emerald-400 pulse-dot"></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: lang('nabidh.statusCard.recordsSubmitted'), value: '1,847', trend: '+12%' },
                      { label: lang('nabidh.statusCard.patientLookups'), value: '524', trend: '+8%' },
                      { label: lang('nabidh.statusCard.syncLatency'), value: '0.3s', trend: lang('nabidh.statusCard.optimal') },
                      { label: lang('nabidh.statusCard.complianceScore'), value: '100%', trend: 'A+' }
                    ].map((stat, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5">
                        <span className="text-xs sm:text-sm text-white/70">{stat.label}</span>
                        <div className="text-right">
                          <span className="font-display font-semibold text-base sm:text-lg">{stat.value}</span>
                          <span className="ml-2 text-xs text-emerald-300">{stat.trend}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-theme-color"></div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 border-2 border-theme-color"></div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 border-2 border-theme-color"></div>
                    </div>
                    <span className="text-xs text-white/60">{lang('nabidh.statusCard.connectedToPart1')} <span className="text-white font-semibold">{lang('nabidh.statusCard.connectedToFacilities')}</span> {lang('nabidh.statusCard.connectedToPart2')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE SUITE ============ */}
        <section id="features" data-animate className={`bg-white py-20 md:py-32 fade-up ${isVisible.features ? 'visible' : ''}`}>
          <div className="w-mobile-container md:w-theme-container mx-auto">
            <div className="max-w-2xl mb-12 md:mb-16">
              <div className="text-xs font-semibold tracking-[0.25em] text-theme-secondary uppercase mb-4">{lang('features.eyebrow')}</div>
              <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl leading-tight text-theme-color">
                {lang('features.titlePart1')}{' '}
                <span className="italic">{lang('features.titleHighlight')}</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-12 gap-6 md:gap-8">
              <div className="md:col-span-4 space-y-2">
                {features.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveFeature(i)}
                    className={`w-full text-left p-4 sm:p-5 rounded-2xl transition-all duration-300 border ${
                      activeFeature === i
                        ? 'bg-theme-color text-white border-theme-color shadow-lg shadow-theme-color/15'
                        : 'bg-white text-theme-color border-theme-color/8 hover:border-theme-color/25'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
                        activeFeature === i ? 'bg-white/15' : 'bg-theme-color/5'
                      }`}>
                        {f.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-base">{f.title}</div>
                      </div>
                      <ChevronIcon className={`w-4 h-4 transition-transform shrink-0 ${activeFeature === i ? 'translate-x-1' : ''}`} />
                    </div>
                  </button>
                ))}
              </div>
              <div className="md:col-span-8">
                <div className="relative bg-white border border-theme-color/8 rounded-3xl overflow-hidden shadow-xl shadow-theme-color/5">
                  <div className="relative h-60 sm:h-72 md:h-96 overflow-hidden">
                    <img
                      src={features[activeFeature].image}
                      alt={features[activeFeature].title}
                      className="w-full h-full object-cover"
                      key={activeFeature}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-theme-color via-theme-color/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-5 sm:p-7">
                      <h3 className="font-display font-medium text-2xl sm:text-3xl md:text-4xl text-white mb-2">
                        {features[activeFeature].title}
                      </h3>
                      <p className="text-white/85 text-sm sm:text-base md:text-lg max-w-xl leading-relaxed">
                        {features[activeFeature].tagline}
                      </p>
                    </div>
                  </div>
                  <div className="p-5 sm:p-7">
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                      {features[activeFeature].points.map((point, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-theme-color/8 flex items-center justify-center shrink-0 mt-0.5">
                            <CheckIcon className="w-3 h-3 text-theme-color" />
                          </div>
                          <span className="text-sm text-theme-color/75 leading-relaxed">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECURITY ============ */}
        <section id="security" data-animate className={`py-20 md:py-32 bg-gradient-to-b from-slate-50 to-white fade-up ${isVisible.security ? 'visible' : ''}`}>
          <div className="w-mobile-container md:w-theme-container mx-auto">
            <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center mb-14 md:mb-16">
              <div className="md:col-span-7">
                <div className="text-xs font-semibold tracking-[0.25em] text-theme-secondary uppercase mb-4">{lang('security.eyebrow')}</div>
                <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl leading-tight text-theme-color">
                  {lang('security.titlePart1')}{' '}
                  <span className="italic text-theme-secondary">{lang('security.titleHighlight')}</span>
                </h2>
                <p className="mt-5 text-base md:text-lg text-theme-color/65 leading-relaxed">
                  {lang('security.description')}
                </p>
              </div>
              <div className="md:col-span-5 relative">
                <div className="relative rounded-3xl overflow-hidden border border-theme-color/8 shadow-xl shadow-theme-color/10">
                  <img
                    src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=700&q=80"
                    alt={lang('security.imageAlt')}
                    className="w-full h-64 sm:h-72 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-theme-color/60 to-transparent"></div>
                  <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-theme-color flex items-center justify-center shrink-0">
                      <ShieldIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-theme-color text-sm sm:text-base">{lang('security.badge.title')}</div>
                      <div className="text-xs text-theme-color/60 mt-0.5">{lang('security.badge.subtitle')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-1 lg:grid-cols-4 gap-5">
              {securityFeatures.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 sm:p-7 border border-theme-color/8 hover:border-theme-color/20 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-theme-color/5">
                  <div className="w-12 h-12 rounded-xl bg-theme-color/5 flex items-center justify-center text-theme-color mb-4">
                    {f.icon}
                  </div>
                  <h3 className="font-display font-semibold text-lg text-theme-color mb-2">{f.title}</h3>
                  <p className="text-sm text-theme-color/65 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ INTEGRATIONS ============ */}
        <section id="integrations" data-animate className={`bg-white py-20 md:py-32 fade-up ${isVisible.integrations ? 'visible' : ''}`}>
          <div className="w-mobile-container md:w-theme-container mx-auto">
            <div className="max-w-2xl mb-12 md:mb-14">
              <div className="text-xs font-semibold tracking-[0.25em] text-theme-secondary uppercase mb-4">{lang('integrations.eyebrow')}</div>
              <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl leading-tight text-theme-color">
                {lang('integrations.titlePart1')}{' '}
                <span className="italic">{lang('integrations.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-base md:text-lg text-theme-color/65">
                {lang('integrations.description')}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {integrations.map((int, i) => (
                <div key={i} className="group relative bg-white border border-theme-color/8 rounded-3xl p-6 sm:p-7 hover:border-theme-color/25 transition-all overflow-hidden">
                  <div className="absolute -right-12 -top-12 w-40 h-40 bg-gradient-to-br from-blue-100/50 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative flex items-start gap-4 sm:gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-theme-color text-white flex items-center justify-center shrink-0 group-hover:rotate-6 transition-transform">
                      {int.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-semibold text-lg sm:text-xl text-theme-color mb-1.5">{int.name}</h3>
                      <p className="text-sm text-theme-color/65 leading-relaxed">{int.desc}</p>
                    </div>
                    <ArrowIcon className="w-5 h-5 text-theme-color/30 group-hover:text-theme-color group-hover:translate-x-1 transition-all shrink-0 hidden sm:block" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section id="contact" className="relative bg-white py-16 md:py-28 overflow-hidden">
          <div className="w-mobile-container md:w-theme-container mx-auto">
            <div className="relative bg-gradient-to-br from-theme-color via-theme-secondary to-theme-color rounded-[2rem] md:rounded-[2.5rem] p-8 overflow-hidden">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
              <div className="absolute -right-20 -top-20 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl blob-anim"></div>
              <div className="absolute -left-20 -bottom-20 w-96 h-96 bg-emerald-400/15 rounded-full blur-3xl blob-anim" style={{ animationDelay: '3s' }}></div>
              <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-center">
                <div className="md:col-span-7">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-6">
                    <SparkleIcon className="w-4 h-4 text-amber-300" />
                    <span className="text-xs font-semibold tracking-wider text-white/90">{lang('badges.readyWhenYouAre')}</span>
                  </div>
                  <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] mb-5">
                    {lang('cta.title')}
                  </h2>
                  <p className="text-base md:text-lg text-white/75 leading-relaxed max-w-xl">
                    {lang('cta.description')}
                  </p>
                  <div className="mt-8 grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                    <a href="mailto:info@majtech.ae" className="group inline-flex items-center justify-center gap-2 bg-white text-theme-color px-7 py-4 rounded-full font-semibold hover:bg-amber-50 transition-all hover:shadow-xl">
                      {lang('cta.bookDemo')}
                      <ArrowIcon className="w-4 h-4 group-hover:translate-x-1 transition" />
                    </a>
                    <a href="mailto:info@majtech.ae" className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/25 text-white px-7 py-4 rounded-full font-semibold hover:bg-white/10 transition">
                      <MailIcon className="w-4 h-4" /> info@majtech.ae
                    </a>
                  </div>
                </div>

                <div className="md:col-span-5 grid sm:grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: <TrendIcon className="w-5 h-5" />, label: lang('cta.stats.reduceAdminTime.label'), value: lang('cta.stats.reduceAdminTime.value') },
                    { icon: <UsersIcon className="w-5 h-5" />, label: lang('cta.stats.onboardStaff.label'), value: lang('cta.stats.onboardStaff.value') },
                    { icon: <ShieldIcon className="w-5 h-5" />, label: lang('cta.stats.dhaCompliance.label'), value: lang('cta.stats.dhaCompliance.value') },
                    { icon: <ClockIcon className="w-5 h-5" />, label: lang('cta.stats.setupTime.label'), value: lang('cta.stats.setupTime.value') }
                  ].map((item, i) => (
                    <div key={i} className="bg-white/8 backdrop-blur border border-white/10 rounded-2xl p-4 sm:p-5">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-amber-300 mb-3">
                        {item.icon}
                      </div>
                      <div className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider">{item.label}</div>
                      <div className="font-display font-semibold text-lg sm:text-xl text-white mt-1">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CliniVista;
